import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import planService, { CouponValidation, Plan } from "@/services/plan.service";
import { PixPaymentDialog } from "@/components/checkout/PixPaymentDialog";
import {
  CreditCardForm,
  toCheckoutCreditCard,
  type CardFormState,
} from "@/components/checkout/CreditCardForm";
import {
  CardHolderForm,
  EMPTY_CARD_HOLDER,
  isCardHolderComplete,
  isCardHolderPhoneValid,
  toCreditCardHolder,
  type CardHolderFormState,
} from "@/components/checkout/CardHolderForm";
import { applyCEPMask, applyPhoneMask } from "@/components/checkout/masks";
import { PaymentMethodSelector } from "@/components/checkout/PaymentMethodSelector";
import { useAuth } from "@/contexts/auth.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Shield,
  Tag,
  CheckCircle,
  AlertCircle,
  Loader2,
  MapPin,
  User,
  Building2,
  Receipt,
  Lock,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import authService from "@/services/auth.service";
import { cn } from "@/lib/utils";

const formatBRL = (value: number) => `R$ ${value.toFixed(2).replace(".", ",")}`;

/**
 * O texto que acompanha todo cupom recusado.
 *
 * `GET /api/v1/public/coupons/validate` não pede autenticação, e até M-062 devolvia um
 * motivo diferente para cada recusa. Isso a tornava um oráculo duplo: "Cupom não
 * encontrado" × "Cupom expirado" distinguia código inexistente de código real, e "Usuário
 * já utilizou um cupom" só era alcançável quando o e-mail informado tinha cadastro. A API
 * passou a responder um motivo único, e a orientação ao cliente — que continua útil —
 * mora aqui, sem depender de qual das sete recusas aconteceu.
 */
const MENSAGEM_DE_CUPOM_RECUSADO =
  "Confira o código digitado. Cupons expirados, esgotados ou já utilizados nesta conta não podem ser aplicados.";

/**
 * Desconto em reais do cupom sobre o valor do pedido.
 * Percentual é limitado a 100% e, em qualquer caso, o desconto
 * nunca ultrapassa o valor do pedido.
 */
const calcCouponDiscount = (
  data: CouponValidation["data"] | null | undefined,
  base: number,
): number => {
  if (!data) return 0;
  const raw =
    data.discount_type === "percentage"
      ? base * (Math.min(Math.max(data.discount_value, 0), 100) / 100)
      : data.discount_value;
  return Math.round(Math.min(Math.max(raw, 0), base) * 100) / 100;
};

/** Ex.: "10% (R$ 12,90)" para percentual, "R$ 50,00" para valor fixo. */
const formatCouponDiscount = (
  data: CouponValidation["data"] | null | undefined,
  base: number,
): string => {
  if (!data) return "";
  const amount = formatBRL(calcCouponDiscount(data, base));
  return data.discount_type === "percentage"
    ? `${data.discount_value}% (${amount})`
    : amount;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user, refreshProfile } = useAuth();
  const {
    plan,
    email: stateEmail,
    name: stateName,
    billingCycle,
  } = (state || {}) as {
    plan: Plan;
    email?: string;
    name?: string;
    billingCycle?: "monthly" | "yearly";
  };

  /**
   * Volta ao painel com o perfil **relido**. O perfil em memória é de antes do checkout, com
   * `subscription` nula, e `ExigePlanoAtivo` devolveria a pessoa para a escolha do plano logo
   * depois de ela assinar. A assinatura nasce em `TRIAL` dentro do próprio checkout — PIX
   * pendente inclusive —, então a releitura já a encontra.
   */
  const irParaOPainel = async () => {
    try {
      await refreshProfile();
    } finally {
      navigate("/dashboard");
    }
  };

  const email = user?.email || stateEmail;
  const name = user?.name || stateName;

  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState<CouponValidation | null>(null);
  const [validating, setValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Pagamento (Asaas)
  const [paymentMethod, setPaymentMethod] = useState<"pix" | "credit_card">(
    "pix",
  );
  const [cardData, setCardData] = useState<CardFormState>({
    holderName: "",
    number: "",
    expiryMonth: "",
    expiryYear: "",
    ccv: "",
  });
  // <M-258> O que o Asaas exige do titular do cartão e o cartão não traz. Não sai do
  // endereço do formulário: aquele é o da loja, e o número dele nem existe como campo —
  // "Rua, número e bairro" vai tudo em `address_line1`, e o Complemento é `address_line2`.
  const [cardHolder, setCardHolder] =
    useState<CardHolderFormState>(EMPTY_CARD_HOLDER);
  const [pixCharge, setPixCharge] = useState<{
    copyPaste?: string | null;
    dueDate?: string | null;
    gatewayPaymentId?: string | null;
  } | null>(null);
  const [showPixDialog, setShowPixDialog] = useState(false);

  const [showDuplicateDocumentModal, setShowDuplicateDocumentModal] =
    useState(false);
  const [duplicateDocumentInfo, setDuplicateDocumentInfo] = useState<{
    documentType: string;
    message: string;
    suggestion: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    personType: "fisica" as "fisica" | "juridica",
    cpf: "",
    cnpj: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    billingData: {
      companyName: "",
      email: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
    },
  });

  const applyCPFMask = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  };

  const applyCNPJMask = (value: string) => {
    return value
      .replace(/\D/g, "")
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === "cpf") {
      value = applyCPFMask(value);
    } else if (field === "cnpj") {
      value = applyCNPJMask(value);
    } else if (field === "zipCode") {
      value = applyCEPMask(value);
    } else if (field.includes("phone")) {
      value = applyPhoneMask(value);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBillingInputChange = (field: string, value: string) => {
    if (field === "zipCode") {
      value = applyCEPMask(value);
    } else if (field === "phone") {
      value = applyPhoneMask(value);
    }

    setFormData((prev) => ({
      ...prev,
      billingData: {
        ...prev.billingData,
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    if (!plan) {
      navigate("/register/plans");
    }
  }, [plan, navigate]);

  const isYearly = billingCycle === "yearly";
  // Precisa espelhar o cálculo do backend, que é quem define o valor cobrado.
  const planAmount = plan
    ? isYearly
      ? plan.price_yearly
      : plan.price_monthly
    : 0;
  const orderBase = plan ? planAmount + (plan.setup_fee || 0) : 0;

  const { total, discountAmount } = useMemo(() => {
    if (!plan) return { total: 0, discountAmount: 0 };

    const discount =
      coupon?.valid && coupon.data
        ? calcCouponDiscount(coupon.data, orderBase)
        : 0;

    return {
      total: Math.max(orderBase - discount, 0),
      discountAmount: discount,
    };
  }, [plan, coupon, orderBase]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: "Erro",
        description: "Digite um código de cupom",
        variant: "destructive",
      });
      return;
    }

    setValidating(true);
    try {
      // Envia o plano para o backend recusar cupom restrito a outro plano
      // já na aplicação, e não só ao finalizar o pedido.
      const result = await planService.validateCoupon(
        couponCode.trim().toUpperCase(),
        email,
        plan?.id,
      );
      setCoupon(result);

      if (result.valid) {
        toast({
          title: "Cupom aplicado!",
          description: `Desconto de ${formatCouponDiscount(result.data, orderBase)} aplicado com sucesso.`,
        });
      } else {
        // A API responde um motivo único e genérico para toda recusa (M-062): distinguir
        // "não existe" de "já utilizado" contava a um visitante anônimo se um código
        // existe e se um e-mail é cliente. O texto de ajuda mora aqui.
        toast({
          title: "Cupom inválido",
          description: MENSAGEM_DE_CUPOM_RECUSADO,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erro ao validar cupom",
        description: "Tente novamente mais tarde",
        variant: "destructive",
      });
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode("");
    toast({
      title: "Cupom removido",
      description: "O desconto foi removido do seu pedido",
    });
  };

  const proceed = async () => {
    setIsProcessing(true);

    try {
      if (!formData.cpf) {
        toast({
          title: "CPF obrigatório",
          description: "Por favor, informe o CPF do responsável",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      if (formData.personType === "juridica" && !formData.cnpj) {
        toast({
          title: "CNPJ obrigatório",
          description: "Para pessoa jurídica, o CNPJ é obrigatório",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      if (formData.personType === "juridica") {
        if (!formData.billingData.companyName) {
          toast({
            title: "Razão Social obrigatória",
            description: "Por favor, informe a Razão Social da empresa",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        if (!formData.billingData.email) {
          toast({
            title: "E-mail da empresa obrigatório",
            description: "Por favor, informe o e-mail da empresa",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }
      }

      if (paymentMethod === "credit_card") {
        const missingCard =
          !cardData.holderName ||
          !cardData.number ||
          !cardData.expiryMonth ||
          !cardData.expiryYear ||
          !cardData.ccv;
        if (missingCard) {
          toast({
            title: "Dados do cartão incompletos",
            description: "Preencha todos os campos do cartão de crédito",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }
        // <M-258> O CEP e o número são os do **titular**, e não os do endereço da loja.
        if (!isCardHolderComplete(cardHolder)) {
          toast({
            title: "Endereço do titular incompleto",
            description:
              "Informe o CEP e o número do endereço do titular do cartão",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }
        if (!isCardHolderPhoneValid(cardHolder)) {
          toast({
            title: "Telefone do titular inválido",
            description:
              "Informe o telefone com DDD, ou deixe em branco para usar o do seu cadastro",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }
      }

      const response = await authService.updateCheckoutData({
        person_type: formData.personType,
        cpf: formData.cpf || undefined,
        cnpj: formData.cnpj || undefined,
        address_line1: formData.addressLine1 || undefined,
        address_line2: formData.addressLine2 || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zip_code: formData.zipCode || undefined,
        billing_data:
          formData.personType === "juridica"
            ? {
                company_name: formData.billingData.companyName || undefined,
                email: formData.billingData.email || undefined,
                phone: formData.billingData.phone || undefined,
                address_line1: formData.billingData.addressLine1 || undefined,
                address_line2: formData.billingData.addressLine2 || undefined,
                city: formData.billingData.city || undefined,
                state: formData.billingData.state || undefined,
                zip_code: formData.billingData.zipCode || undefined,
              }
            : undefined,
        plan_id: plan.id,
        billing_cycle: billingCycle || "monthly",
        coupon_code: coupon?.valid ? coupon.data?.code : undefined,
        total_amount: total,
        discount_amount: discountAmount,
        payment_method: paymentMethod,
        credit_card:
          paymentMethod === "credit_card"
            ? toCheckoutCreditCard(cardData)
            : undefined,
        credit_card_holder:
          paymentMethod === "credit_card"
            ? toCreditCardHolder(cardHolder)
            : undefined,
      });

      setIsProcessing(false);

      // PIX: exibir QR/copia-e-cola e aguardar pagamento.
      if (response.payment?.pix_copy_paste || response.payment?.pix_qr_image) {
        setPixCharge({
          copyPaste: response.payment.pix_copy_paste,
          dueDate: response.payment.due_date,
          gatewayPaymentId: response.payment.gateway_payment_id,
        });
        setShowPixDialog(true);
        return;
      }

      // Cartão (ou gateway desabilitado): finalizar.
      toast({
        title: "Pedido finalizado com sucesso!",
        description:
          plan.trial_days > 0
            ? `Sua conta foi criada com ${plan.trial_days} dias gratuitos. Aproveite todos os recursos!`
            : "Sua conta foi criada. Aproveite todos os recursos!",
      });
      setTimeout(() => {
        void irParaOPainel();
      }, 2000);
    } catch (error: unknown) {
      setIsProcessing(false);
      console.error("Erro ao atualizar dados do checkout:", error);

      const err = error as {
        status?: number;
        detail?: string;
        response?: { status?: number; data?: { detail?: string } };
      };
      // hey-api (throwOnError) lança o corpo {detail} direto; axios usa response.data.detail
      const detail =
        typeof err.detail === "string"
          ? err.detail
          : typeof err.response?.data?.detail === "string"
            ? err.response.data.detail
            : undefined;
      const statusCode = err.response?.status ?? err.status;

      if (
        statusCode === 409 &&
        typeof detail === "string" &&
        detail.startsWith("DUPLICATE_")
      ) {
        const parts = detail.split("|");
        const documentType = parts[0].replace("DUPLICATE_", "");
        const message = parts[1] || "Documento já cadastrado";
        const suggestion = parts[2] || "Faça login ou recupere sua senha";

        setDuplicateDocumentInfo({
          documentType,
          message,
          suggestion,
        });
        setShowDuplicateDocumentModal(true);
        return;
      }

      const errorMessage =
        typeof detail === "string"
          ? detail
          : "Houve um problema ao atualizar seus dados. Tente novamente.";

      toast({
        title: "Erro ao salvar dados",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  if (!plan) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() =>
                navigate("/register/plans", { state: { email, name } })
              }
              className="text-slate-300 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <img
                src="/logoSidebar.png"
                alt="Painel do E-Commerce"
                className="h-8"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Lock className="h-4 w-4 text-emerald-400" />
              Checkout seguro
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-2 text-sm text-slate-500">
          <span>Cadastro</span>
          <span className="text-slate-300">→</span>
          <span>Plano</span>
          <span className="text-slate-300">→</span>
          <span className="text-[#2869C1] font-medium">Checkout</span>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Data Card */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-slate-900">
                  <div className="p-2 bg-[#2869C1]/10 rounded-lg">
                    <User className="h-5 w-5 text-[#2869C1]" />
                  </div>
                  Dados Pessoais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      Nome completo
                    </Label>
                    <div className="mt-1.5 p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900">
                      {name || "Não informado"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      E-mail
                    </Label>
                    <div className="mt-1.5 p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900">
                      {email || "Não informado"}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      Tipo de cadastro
                    </Label>
                    <Select
                      value={formData.personType}
                      onValueChange={(value) =>
                        handleInputChange(
                          "personType",
                          value as "fisica" | "juridica",
                        )
                      }
                    >
                      <SelectTrigger className="mt-1.5 bg-white border-slate-200">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fisica">Pessoa Física</SelectItem>
                        <SelectItem value="juridica">
                          Pessoa Jurídica
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      CPF{" "}
                      {formData.personType === "juridica"
                        ? "do Responsável"
                        : ""}{" "}
                      *
                    </Label>
                    <Input
                      value={formData.cpf}
                      onChange={(e) => handleInputChange("cpf", e.target.value)}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      className="mt-1.5 border-slate-200 bg-white"
                      required
                    />
                  </div>
                </div>

                {formData.personType === "juridica" && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-600">
                        CNPJ da Empresa *
                      </Label>
                      <Input
                        value={formData.cnpj}
                        onChange={(e) =>
                          handleInputChange("cnpj", e.target.value)
                        }
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        className="mt-1.5 border-slate-200 bg-white"
                        required
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Address Card */}
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-slate-900">
                  <div className="p-2 bg-[#2869C1]/10 rounded-lg">
                    <MapPin className="h-5 w-5 text-[#2869C1]" />
                  </div>
                  Endereço{" "}
                  {formData.personType === "juridica" ? "do Responsável" : ""}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-600">
                    Endereço completo
                  </Label>
                  <Input
                    value={formData.addressLine1}
                    onChange={(e) =>
                      handleInputChange("addressLine1", e.target.value)
                    }
                    placeholder="Rua, número e bairro"
                    className="mt-1.5 border-slate-200 bg-white"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-600">
                    Complemento
                  </Label>
                  <Input
                    value={formData.addressLine2}
                    onChange={(e) =>
                      handleInputChange("addressLine2", e.target.value)
                    }
                    placeholder="Apartamento, sala, etc. (opcional)"
                    className="mt-1.5 border-slate-200 bg-white"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      Cidade
                    </Label>
                    <Input
                      value={formData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      placeholder="Cidade"
                      className="mt-1.5 border-slate-200 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      Estado
                    </Label>
                    <Input
                      value={formData.state}
                      onChange={(e) =>
                        handleInputChange("state", e.target.value.toUpperCase())
                      }
                      placeholder="UF"
                      maxLength={2}
                      className="mt-1.5 border-slate-200 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      CEP
                    </Label>
                    <Input
                      value={formData.zipCode}
                      onChange={(e) =>
                        handleInputChange("zipCode", e.target.value)
                      }
                      placeholder="00000-000"
                      maxLength={9}
                      className="mt-1.5 border-slate-200 bg-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing Data Card - PJ Only */}
            {formData.personType === "juridica" && (
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-slate-900">
                    <div className="p-2 bg-[#2869C1]/10 rounded-lg">
                      <Building2 className="h-5 w-5 text-[#2869C1]" />
                    </div>
                    Dados para Faturamento
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-600">
                        Razão Social *
                      </Label>
                      <Input
                        value={formData.billingData.companyName}
                        onChange={(e) =>
                          handleBillingInputChange(
                            "companyName",
                            e.target.value,
                          )
                        }
                        placeholder="Nome da empresa"
                        className="mt-1.5 border-slate-200 bg-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-600">
                        E-mail da Empresa *
                      </Label>
                      <Input
                        type="email"
                        value={formData.billingData.email}
                        onChange={(e) =>
                          handleBillingInputChange("email", e.target.value)
                        }
                        placeholder="email@empresa.com"
                        className="mt-1.5 border-slate-200 bg-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-600">
                        Telefone da Empresa
                      </Label>
                      <Input
                        value={formData.billingData.phone}
                        onChange={(e) =>
                          handleBillingInputChange("phone", e.target.value)
                        }
                        placeholder="(00) 00000-0000"
                        className="mt-1.5 border-slate-200 bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      Endereço da Empresa
                    </Label>
                    <Input
                      value={formData.billingData.addressLine1}
                      onChange={(e) =>
                        handleBillingInputChange("addressLine1", e.target.value)
                      }
                      placeholder="Rua, número e bairro"
                      className="mt-1.5 border-slate-200 bg-white"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-600">
                      Complemento
                    </Label>
                    <Input
                      value={formData.billingData.addressLine2}
                      onChange={(e) =>
                        handleBillingInputChange("addressLine2", e.target.value)
                      }
                      placeholder="Sala, andar, etc. (opcional)"
                      className="mt-1.5 border-slate-200 bg-white"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-600">
                        Cidade
                      </Label>
                      <Input
                        value={formData.billingData.city}
                        onChange={(e) =>
                          handleBillingInputChange("city", e.target.value)
                        }
                        placeholder="Cidade"
                        className="mt-1.5 border-slate-200 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-600">
                        Estado
                      </Label>
                      <Input
                        value={formData.billingData.state}
                        onChange={(e) =>
                          handleBillingInputChange(
                            "state",
                            e.target.value.toUpperCase(),
                          )
                        }
                        placeholder="UF"
                        maxLength={2}
                        className="mt-1.5 border-slate-200 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-600">
                        CEP
                      </Label>
                      <Input
                        value={formData.billingData.zipCode}
                        onChange={(e) =>
                          handleBillingInputChange("zipCode", e.target.value)
                        }
                        placeholder="00000-000"
                        maxLength={9}
                        className="mt-1.5 border-slate-200 bg-white"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Order Summary */}
          <div>
            <Card className="sticky top-24 border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-slate-900">
                  <div className="p-2 bg-[#2869C1]/10 rounded-lg">
                    <Receipt className="h-5 w-5 text-[#2869C1]" />
                  </div>
                  Resumo do Pedido
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selected Plan */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="font-semibold text-slate-900 text-lg">
                    {plan.name}
                  </div>
                  {plan.description && (
                    <p className="text-sm text-slate-500 mt-1">
                      {plan.description}
                    </p>
                  )}
                  {plan.trial_days > 0 && (
                    <Badge
                      variant="secondary"
                      className="mt-3 bg-emerald-100 text-emerald-700 border-0"
                    >
                      {plan.trial_days} dias grátis
                    </Badge>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 border-t border-slate-200 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">
                      {isYearly ? "Anuidade" : "Mensalidade"}
                    </span>
                    <span className="text-slate-900">
                      {formatBRL(planAmount)}
                    </span>
                  </div>

                  {plan.setup_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Taxa de setup</span>
                      <span className="text-slate-900">
                        R$ {plan.setup_fee.toFixed(2).replace(".", ",")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Coupon Section */}
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Tag className="h-4 w-4 text-[#2869C1]" />
                    Cupom de desconto
                  </div>

                  <div className="flex gap-2">
                    <Input
                      placeholder="Código do cupom"
                      value={couponCode}
                      onChange={(e) =>
                        setCouponCode(e.target.value.toUpperCase())
                      }
                      className="flex-1 text-sm border-slate-200"
                      disabled={validating}
                    />
                    <Button
                      onClick={validateCoupon}
                      disabled={validating || !couponCode.trim()}
                      size="sm"
                      className="px-4 bg-[#2869C1] hover:bg-[#1d5299]"
                    >
                      {validating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Aplicar"
                      )}
                    </Button>
                  </div>

                  {coupon && (
                    <div
                      className={cn(
                        "p-3 rounded-lg border text-sm",
                        coupon.valid
                          ? "bg-emerald-50 border-emerald-200"
                          : "bg-red-50 border-red-200",
                      )}
                    >
                      <div
                        className={cn(
                          "flex items-center gap-2 font-medium",
                          coupon.valid ? "text-emerald-700" : "text-red-700",
                        )}
                      >
                        {coupon.valid ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        {coupon.valid ? "Cupom aplicado!" : "Cupom inválido"}
                      </div>
                      <p
                        className={cn(
                          "text-sm mt-1",
                          coupon.valid ? "text-emerald-600" : "text-red-600",
                        )}
                      >
                        {coupon.valid
                          ? `Desconto de ${formatCouponDiscount(coupon.data, orderBase)}`
                          : MENSAGEM_DE_CUPOM_RECUSADO}
                      </p>
                      {coupon.valid && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={removeCoupon}
                          className="mt-2 h-7 text-xs hover:bg-emerald-100"
                        >
                          Remover cupom
                        </Button>
                      )}
                    </div>
                  )}

                  {coupon?.valid && coupon.data && (
                    <div className="flex justify-between text-sm text-emerald-600 pt-2 border-t border-slate-200">
                      <span>
                        Desconto ({coupon.data.code}
                        {coupon.data.discount_type === "percentage"
                          ? ` · ${coupon.data.discount_value}%`
                          : ""}
                        )
                      </span>
                      <span>-{formatBRL(discountAmount)}</span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-slate-900">
                      Total
                    </span>
                    <span className="text-2xl font-bold text-[#2869C1]">
                      R$ {total.toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                  {plan.trial_days > 0 && (
                    <p className="text-xs text-slate-500 mt-2">
                      Cobrança apenas após {plan.trial_days} dias de teste
                    </p>
                  )}
                </div>

                {/* Legal Text */}
                <div className="text-center py-3">
                  <p className="text-xs text-slate-500">
                    Ao finalizar, você concorda com os{" "}
                    <a
                      href="https://www.paineldoecommerce.com.br/termos-de-uso/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2869C1] hover:underline"
                    >
                      Termos de Uso
                    </a>{" "}
                    e{" "}
                    <a
                      href="https://www.paineldoecommerce.com.br/politica-de-privacidade/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2869C1] hover:underline"
                    >
                      Política de Privacidade
                    </a>
                  </p>
                </div>

                {/* Payment method selector */}
                <div className="space-y-3">
                  <Label>Forma de pagamento</Label>
                  <PaymentMethodSelector
                    value={paymentMethod}
                    onChange={setPaymentMethod}
                  />
                  {paymentMethod === "credit_card" && (
                    <>
                      <CreditCardForm value={cardData} onChange={setCardData} />
                      <CardHolderForm
                        value={cardHolder}
                        onChange={setCardHolder}
                      />
                    </>
                  )}
                </div>

                {/* Checkout Button */}
                <Button
                  className="w-full py-6 text-base font-semibold bg-[#9CCB3B] hover:bg-[#8bba34]"
                  onClick={proceed}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processando...
                    </>
                  ) : (
                    "Finalizar pedido"
                  )}
                </Button>

                {/* Security Badge */}
                <div className="text-center pt-4 border-t border-slate-200 space-y-3">
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                    <Shield className="h-4 w-4 text-emerald-500" />
                    <span>Pagamento 100% seguro e criptografado</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Duplicate Document Modal */}
      <AlertDialog
        open={showDuplicateDocumentModal}
        onOpenChange={setShowDuplicateDocumentModal}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {duplicateDocumentInfo?.documentType} já cadastrado
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              {duplicateDocumentInfo?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              {duplicateDocumentInfo?.suggestion}
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  setShowDuplicateDocumentModal(false);
                  navigate("/login");
                }}
                className="w-full bg-[#2869C1] hover:bg-[#1d5299]"
              >
                Fazer Login
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  setShowDuplicateDocumentModal(false);
                  navigate("/forgot-password");
                }}
                className="w-full"
              >
                Recuperar Senha
              </Button>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setShowDuplicateDocumentModal(false)}
              className="bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PIX Payment Dialog */}
      <PixPaymentDialog
        open={showPixDialog}
        onOpenChange={setShowPixDialog}
        copyPaste={pixCharge?.copyPaste}
        dueDate={pixCharge?.dueDate}
        gatewayPaymentId={pixCharge?.gatewayPaymentId}
        description={
          <>
            {plan && plan.trial_days > 0
              ? `Seu acesso já está liberado com ${plan.trial_days} dias gratuitos.`
              : "Seu acesso é liberado assim que o pagamento for confirmado."}
          </>
        }
        footerAction={{
          label: "Ir para o painel",
          onClick: () => {
            setShowPixDialog(false);
            void irParaOPainel();
          },
        }}
        onPaid={() => {
          setShowPixDialog(false);
          toast({
            title: "Pagamento confirmado!",
            description: "Sua assinatura está ativa. Redirecionando...",
          });
          setTimeout(() => void irParaOPainel(), 1200);
        }}
      />
    </div>
  );
};

export default CheckoutPage;
